import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import IntegrationCardGrid from "../../components/Integration/IntegrationCardGrid";
import IntegrationTypeTable from "../../components/Integration/IntegrationTypeTable";
import { getIntegrationConfig } from "../../config/integrationConfig";
import DynamicBreadcrumbs from "../../components/Breadcrumbs/DynamicBreadcrumbs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";

const ActiveDirectoryPage = () => {
  const integrationType = "active-directory"; // This is fixed for this page
  const integrationConfig = getIntegrationConfig(integrationType);
  const navigate = useNavigate();
  const location = useLocation();

  const [integrationsData, setIntegrationsData] = useState([]);
  const [statsData, setStatsData] = useState({});
  const [findingsData, setFindingsData] = useState([]);

  useEffect(() => {
    if (integrationConfig) {
      // In a real application, you would fetch data from your API
      // For now, we use mock data from the config
      setIntegrationsData(integrationConfig.mockIntegrationsData || []);
      setStatsData(integrationConfig.mockStatsData || {});
      setFindingsData(integrationConfig.mockFindingsData || []);
    }
  }, [integrationConfig]);

  if (!integrationConfig) {
    return <div>Loading configuration...</div>;
  }

  // Render stats cards
  const renderStatsCards = () => {
    return integrationConfig.statsCards?.map((stat, index) => {
      let value = statsData[stat.dataPath];
      if (stat.type === "progress_bar") {
        // Ensure value exists and has the expected structure
        if (!value || typeof value !== "object") {
          value = { protected: 0, unprotected: 0 };
        }
        const protectedCount = value.protected || 0;
        const unprotectedCount = value.unprotected || 0;
        const total = protectedCount + unprotectedCount;
        const displayValue = `${protectedCount} Protected Accounts`;
        return (
          <Card key={index} className="flex-1 min-w-[200px]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{displayValue}</div>
              <div className="text-sm text-gray-600 mt-1">{unprotectedCount} Unprotected</div>
              <div className="mt-2 h-2 w-full bg-gray-200 rounded-full">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{
                    width: total > 0 ? `${(protectedCount / total) * 100}%` : "0%",
                  }}
                ></div>
              </div>
            </CardContent>
          </Card>
        );
      }
      return (
        <Card key={index} className="flex-1 min-w-[200px]">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {stat.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{value ?? 0}</div>
          </CardContent>
        </Card>
      );
    });
  };

  return (
    <div className="p-6">
      <DynamicBreadcrumbs />
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        Service Accounts Identity Management
      </h1>
      <p className="text-gray-600 mb-6">
        Active Directory security monitoring and identity management • {integrationsData.length} configured
      </p>

      <Separator className="my-6" />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        {renderStatsCards()}
      </div>

      <Separator className="my-6" />

      {/* Integration List (Domains) */}
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">
        Integrated {integrationConfig.displayName} Domains
      </h2>
      {integrationConfig.viewType === "table" ? (
        <IntegrationTypeTable
          data={integrationsData}
          columns={integrationConfig.integrationTableColumns || []}
          onRowClick={(row) => {
            const id = row.id || row.integration_id;
            if (id) {
              // Check current path to determine navigation route
              if (location.pathname.startsWith("/integration/active-directory")) {
                navigate(`/integration/active-directory/${id}`);
              } else {
                navigate(`/identity-management/active-directory/${id}`);
              }
            }
          }}
          isLoading={false}
        />
      ) : (
        <IntegrationCardGrid
          integrations={integrationsData}
          integrationType={integrationType}
        />
      )}

      <Separator className="my-6" />

    
    </div>
  );
};

export default ActiveDirectoryPage;
